import {
  createBrowserRouter,
  Navigate,
  useLocation,
  useRouteError,
} from 'react-router-dom';
import { LoginPage, MainGamePage, PrivacyPolicyPage, TermsOfServicePage } from '@pages';
import { useAuthStore } from '@app/stores';

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to={`/login${location.search}`} replace />;
  }

  return <>{children}</>;
};

const RootRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/game${location.search}`} replace />;
};

const RouteErrorBoundary = () => {
  const error = useRouteError() as Error;

  const handleCopyError = () => {
    const errorText = `Error: ${error.message}\n\nStack:\n${error.stack || 'No stack trace available'}`;
    navigator.clipboard.writeText(errorText).then(() => {
      alert('Error copied to clipboard!');
    });
  };

  return (
    <div className="w-full h-screen bg-gradient-to-b from-indigo-950 via-purple-950 to-indigo-950 flex items-center justify-center p-4">
      <div className="relative w-[339px] flex flex-col items-center p-3 isolate bg-[#C2CCDF] border-2 border-[rgba(46,64,86,0.2)] rounded-2xl">
        {/* Content */}
        <div className="flex flex-col items-center gap-4 w-full max-w-[307px]">
          {/* Error Icon Container - Overlapping */}
          <div className="absolute top-7 z-10">
            <div className="text-5xl">⚠️</div>
          </div>

          {/* Error Info Container */}
          <div className="flex flex-col items-center pt-16 pb-4 px-4 w-full bg-[#8391A6] rounded-xl gap-3">
            <h2 className="font-lilita text-[24px] text-white text-center [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000]">
              Oops! Error Detected
            </h2>
            <p className="font-lilita text-[14px] text-red-300 text-center leading-relaxed [text-shadow:0.5px_1px_0px_#000] break-words w-full">
              {error.message}
            </p>
          </div>

          {/* Copy Error Button */}
          <button
            onClick={handleCopyError}
            className="relative w-full flex items-center justify-center gap-2 px-4 py-3 h-12 bg-[#6B7A8E] hover:bg-[#5A6778] border-2 border-black rounded-xl transition-colors overflow-hidden"
          >
            <div className="absolute inset-x-2 top-2 h-7 bg-white/20 rounded-lg z-0" />
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="relative z-10"
            >
              <rect
                x="9"
                y="9"
                width="13"
                height="13"
                rx="2"
                stroke="white"
                strokeWidth="2"
                fill="none"
              />
              <path
                d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5"
                stroke="white"
                strokeWidth="2"
                fill="none"
              />
            </svg>
            <span className="font-lilita text-[16px] text-white relative z-10 [text-shadow:1px_1px_0px_#000] [-webkit-text-stroke:0.5px_#000]">
              Copy Error Details
            </span>
          </button>

          {/* Retry Button */}
          <button
            onClick={() => window.location.reload()}
            className="relative flex items-center justify-center px-6 py-3 gap-3 isolate h-14 border-2 border-black rounded-xl overflow-hidden w-full"
            style={{
              background:
                'linear-gradient(135deg, #FF7A00 0%, #FFA500 49.04%, #FFD700 100%)',
            }}
          >
            <div className="absolute inset-x-2 top-2 h-8 bg-white/30 rounded-lg z-0" />
            <span className="font-lilita text-[24px] leading-8 text-white relative z-10 [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000]">
              Try Again
            </span>
          </button>

          {/* Helper Text */}
          <p className="text-center text-white text-sm -mt-2 mb-1 [text-shadow:0.5px_1px_0px_#000]">
            Commander, reload the game to continue
          </p>
        </div>
      </div>
    </div>
  );
};

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/game',
    element: (
      <ProtectedRoute>
        <MainGamePage />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/privacy-policy',
    element: <PrivacyPolicyPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/terms-of-service',
    element: <TermsOfServicePage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/',
    element: <RootRedirect />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '*',
    element: <RootRedirect />,
    errorElement: <RouteErrorBoundary />,
  },
]);
